import { useEffect, useRef, useState } from "react";
import { FaPencilAlt, FaSave } from "@/utils/icons";
import {
  READER_CONFIDENCE_ANCHORS,
  READER_CONFIDENCE_LABEL,
  parseReaderConfidence,
} from "@/utils/readerConfidence";

/** Line between a card's mask data and its confidence row (ENG-277). */
export const CONFIDENCE_SEPARATOR_CLASS = "border-t border-border pt-1.5";

/**
 * A saved card's rating with an inline re-rate (ENG-277). Pencil opens a 1–100
 * field; Enter or the save icon saves; Esc or a click outside cancels. Saving is
 * an attributes-only update, so the mask and its review label are kept.
 */
export function CardConfidenceRow({
  value,
  disabled = false,
  onSave,
}: {
  value: number | null;
  disabled?: boolean;
  /** Rejects when the update failed; the field stays open for a retry. */
  onSave: (value: number) => Promise<void>;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const savingRef = useRef(false);
  const editing = draft != null;
  const parsed = parseReaderConfidence(draft ?? "");
  const invalidText = editing && draft.trim() !== "" && parsed == null;

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);
  // A lock that arrives mid-edit (case completed, editor opened) closes the field;
  // our own save also locks the card, so that one keeps the field and its error.
  useEffect(() => {
    if (disabled && !savingRef.current) {
      setDraft(null);
      setError(null);
    }
  }, [disabled]);

  const cancel = () => {
    if (saving) return;
    setDraft(null);
    setError(null);
  };
  const save = async () => {
    if (parsed == null || saving || disabled) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await onSave(parsed);
      setDraft(null);
    } catch {
      setError("Could not save. Please retry.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="card-confidence"
      className={`${CONFIDENCE_SEPARATOR_CLASS} flex flex-col gap-0.5 font-mono text-[10px] text-fg-muted`}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onBlur={(event) => {
        if (editing && !event.currentTarget.contains(event.relatedTarget as Node | null)) cancel();
      }}
    >
      {editing ? (
        <div className="flex items-center gap-1.5">
          <span className="uppercase tracking-wide">Confidence</span>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            aria-label={READER_CONFIDENCE_LABEL}
            aria-invalid={invalidText || undefined}
            placeholder="1–100"
            title={READER_CONFIDENCE_ANCHORS}
            value={draft}
            disabled={disabled || saving}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void save();
              } else if (event.key === "Escape") {
                event.preventDefault();
                cancel();
              }
            }}
            className={`h-6 w-12 rounded-[2px] border bg-bg-2 px-1.5 text-center text-[11px] text-fg tabular-nums outline-none focus:border-signal disabled:opacity-40 ${
              invalidText ? "border-warn/60" : "border-border-strong"
            }`}
          />
          <button
            type="button"
            aria-label="Save confidence"
            title="Save confidence"
            disabled={disabled || saving || parsed == null}
            // Keep focus in the field so the click isn't taken as a click outside.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => void save()}
            className="h-6 w-6 inline-flex items-center justify-center rounded-[2px] text-fg-muted enabled:hover:text-fg enabled:hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {/* The button's label names it; the icon isn't a card image. */}
            <span aria-hidden="true">
              <FaSave className="w-3 h-3" />
            </span>
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <span className="uppercase tracking-wide">
            Confidence: <span className="text-fg tabular-nums">{value ?? "—"}</span>
          </span>
          <button
            type="button"
            aria-label="Change confidence"
            title="Change confidence"
            disabled={disabled}
            onClick={() => {
              // Starts empty: save stays disabled until a new rating is typed.
              setError(null);
              setDraft("");
            }}
            className="h-5 w-5 inline-flex items-center justify-center rounded-[2px] text-fg-muted enabled:hover:text-fg enabled:hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span aria-hidden="true">
              <FaPencilAlt className="w-2.5 h-2.5" />
            </span>
          </button>
        </div>
      )}
      {invalidText && <span className="text-[9px] text-warn">Whole number 1–100 only</span>}
      {error && (
        <span role="alert" className="text-[9px] text-red-400">
          {error}
        </span>
      )}
    </div>
  );
}
